import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { getPharmacists } from '@/features/auth/apis/pharmacy'
import { PharmacistListResponse } from '@/features/auth/types/pharmacy'

interface UsePharmacistsQueryReturn {
  pharmacists: PharmacistListResponse | null
  data: PharmacistListResponse | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
  isFetching: boolean
  isSuccess: boolean
}

/**
 * 薬剤師一覧取得クエリ（TanStack Query）
 *
 * 責務:
 * - 薬剤師一覧データの取得
 * - キャッシュ管理
 *
 * 使用例:
 * ```tsx
 * const { pharmacists, isLoading, error, refetch } = usePharmacistsQuery()
 * ```
 */
export const usePharmacistsQuery = (): UsePharmacistsQueryReturn => {
  const query = useQuery({
    queryKey: queryKeys.pharmacy.pharmacists(),
    queryFn: async (): Promise<PharmacistListResponse> => {
      const data = await getPharmacists()
      return data
    },
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    pharmacists: query.data ?? null,
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  }
}
